/**
 * Sidebar filter shortcuts: the filters a user chose to pin under "Starred".
 * Nothing is pinned by default; the user adds and removes freely. Stored per user
 * in this browser (same place the other small UI preferences live).
 */
import {
  ASSIST_FILTER_AI_READY,
  MANUAL_WORKFLOW_FILTER_ID,
  buildActionItemsListPath,
} from "../actionItemHelpers";

const STORAGE_KEY_PREFIX = "starmesh.pinnedFilters.v1:";

export const PINNED_FILTER_KINDS = ["source", "category", "assist"];

/**
 * localStorage key for one user's pinned filters.
 *
 * @example
 * pinnedFiltersStorageKey("u-1"); // "starmesh.pinnedFilters.v1:u-1"
 */
export const pinnedFiltersStorageKey = (userId) =>
  `${STORAGE_KEY_PREFIX}${String(userId || "anon").trim() || "anon"}`;

const sanitize = (list) => {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).reduce((acc, entry) => {
    const kind = String(entry?.kind || "");
    const id = String(entry?.id || "").trim();
    const label = String(entry?.label || "").trim();
    const key = `${kind}:${id}`;
    if (!PINNED_FILTER_KINDS.includes(kind) || !id || !label || seen.has(key)) return acc;
    seen.add(key);
    acc.push({
      kind,
      id,
      label,
      workflowType: String(entry?.workflowType || ""),
    });
    return acc;
  }, []);
};

/**
 * @example
 * readPinnedFilters("u-1"); // [] until something is pinned
 */
export const readPinnedFilters = (userId) => {
  try {
    return sanitize(JSON.parse(window.localStorage.getItem(pinnedFiltersStorageKey(userId)) || "[]"));
  } catch (_) {
    return [];
  }
};

/**
 * @example
 * writePinnedFilters("u-1", [{ kind: "source", id: "wf-granola", label: "Granola" }]);
 */
export const writePinnedFilters = (userId, list) => {
  const clean = sanitize(list);
  try {
    window.localStorage.setItem(pinnedFiltersStorageKey(userId), JSON.stringify(clean));
  } catch (_) {
    // Storage blocked: the pins last for this session only.
  }
  return clean;
};

/**
 * Add one filter (no-op when already pinned).
 *
 * @example
 * addPinnedFilter([], { kind: "source", id: "wf-granola", label: "Granola" });
 * // [{ kind: "source", id: "wf-granola", label: "Granola", workflowType: "" }]
 */
export const addPinnedFilter = (list, entry) => sanitize([...(list || []), entry]);

/**
 * Remove one filter.
 *
 * @example
 * removePinnedFilter([{ kind: "source", id: "a", label: "A" }], { kind: "source", id: "a" }); // []
 */
export const removePinnedFilter = (list, entry) =>
  sanitize(list).filter((item) => !(item.kind === entry.kind && item.id === entry.id));

/**
 * Filters the picker offers: connected sources, categories, and AI-ready.
 *
 * @example
 * buildPinnedFilterOptions({ workflowEntries: [{ id: "wf-g", name: "Granola", workflowType: "granola_daily_digest" }], categoryCatalog: ["Work"] });
 * // { source: [{ kind: "source", id: "wf-g", label: "Granola", ... }], category: [{ kind: "category", id: "Work", label: "Work" }], assist: [...] }
 */
export const buildPinnedFilterOptions = ({
  workflowEntries = [],
  hasManualItems = false,
  categoryCatalog = [],
} = {}) => {
  const source = (workflowEntries || []).map((entry) => ({
    kind: "source",
    id: entry.id,
    label: entry.name,
    workflowType: entry.workflowType || "",
  }));
  if (hasManualItems) {
    source.push({ kind: "source", id: MANUAL_WORKFLOW_FILTER_ID, label: "Added by you" });
  }
  const category = (categoryCatalog || [])
    .map((label) => String(label || "").trim())
    .filter(Boolean)
    .map((label) => ({ kind: "category", id: label, label }));
  const assist = [{ kind: "assist", id: ASSIST_FILTER_AI_READY, label: "AI Actions ready" }];
  return { source, category, assist };
};

/**
 * Task-list path for a pinned filter.
 *
 * @example
 * buildPinnedFilterPath({ kind: "source", id: "wf-g" }); // "/business/action-items?workflow=wf-g"
 */
export const buildPinnedFilterPath = (entry) => {
  if (entry.kind === "source") return buildActionItemsListPath({ workflow: entry.id });
  if (entry.kind === "category") return buildActionItemsListPath({ category: entry.id });
  return buildActionItemsListPath({ assist: entry.id });
};

/**
 * True when the URL is currently filtered by exactly this pinned filter.
 *
 * @example
 * isPinnedFilterActive({ kind: "source", id: "wf-g" }, "?workflow=wf-g"); // true
 */
export const isPinnedFilterActive = (entry, search = "") => {
  const params = new URLSearchParams(String(search || ""));
  if (entry.kind === "source") return params.get("workflow") === entry.id;
  if (entry.kind === "category") return params.get("category") === entry.id;
  return params.get("assist") === entry.id;
};
